/**
 * API client for BeatDuel SPAs (public + admin).
 * Auto-attaches Bearer token, silent 401 refresh before logout.
 * Shared via $shared/api.ts → spa-shared/.
 */

import { getToken, logout, refreshAccessToken } from "$lib/auth.svelte";

const API_BASE = "/api/v1";

/** Unwrap standard API envelope: {success, data, pagination?} → data with pagination re-attached */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapEnvelope(body: Record<string, any>): unknown {
  if (body && typeof body === "object" && "success" in body && "data" in body) {
    const unwrapped = body.data;
    if (
      body.pagination &&
      typeof unwrapped === "object" &&
      unwrapped !== null
    ) {
      unwrapped.pagination = body.pagination;
    }
    return unwrapped;
  }
  return body;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function fetchApi<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Default to JSON content type for non-FormData bodies
  if (options.body && !(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    // Try silent token refresh before logging out
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      headers["Authorization"] = `Bearer ${getToken()}`;
      const retry = await fetch(url, { ...options, headers });
      if (retry.ok) return unwrapEnvelope(await retry.json()) as T;
      if (retry.status !== 401) {
        const body = await retry.json().catch(() => ({}));
        const msg =
          body.error?.message ||
          body.message ||
          `Request failed (${retry.status})`;
        throw new ApiError(retry.status, msg, body);
      }
    }
    logout();
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      body.error?.message || body.message || `Request failed (${res.status})`;
    throw new ApiError(res.status, msg, body);
  }

  return unwrapEnvelope(await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => fetchApi<T>(path),

  post: <T>(path: string, body?: unknown) =>
    fetchApi<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(path: string, body?: unknown) =>
    fetchApi<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(path: string, body?: unknown) =>
    fetchApi<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(path: string) => fetchApi<T>(path, { method: "DELETE" }),

  /** Fetch a binary response (e.g. PNG image) with auth handling. */
  getBlob: async (path: string): Promise<Blob> => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
    let res = await fetch(url, { headers });

    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        headers["Authorization"] = `Bearer ${getToken()}`;
        res = await fetch(url, { headers });
        if (res.ok) return res.blob();
      }
      logout();
      throw new ApiError(401, "Unauthorized");
    }

    if (!res.ok)
      throw new ApiError(res.status, `Request failed (${res.status})`);
    return res.blob();
  },

  /**
   * Upload FormData with progress tracking via XHR.
   * onProgress receives 0–100 percentage.
   */
  upload: <T>(
    path: string,
    formData: FormData,
    onProgress?: (pct: number) => void,
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = path.startsWith("http") ? path : `${API_BASE}${path}`;

      xhr.open("POST", url);

      const token = getToken();
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }

      xhr.onload = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: Record<string, any>;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = {};
        }

        if (xhr.status === 401) {
          logout();
          reject(new ApiError(401, "Unauthorized"));
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          const msg =
            data.error?.message ||
            data.message ||
            `Request failed (${xhr.status})`;
          reject(new ApiError(xhr.status, msg, data));
          return;
        }

        resolve(unwrapEnvelope(data) as T);
      };

      xhr.onerror = () => {
        reject(new ApiError(0, "Network error"));
      };

      xhr.send(formData);
    });
  },
};

/**
 * Parse Yii2 validation errors into a field → message map.
 */
export function parseValidationErrors(data: unknown): Record<string, string> {
  const errors: Record<string, string> = {};
  if (Array.isArray(data)) {
    for (const item of data) {
      if (item.field && item.message) {
        errors[item.field] = item.message;
      }
    }
  }
  return errors;
}
